import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ShieldCheck, MessageCircle, Calculator, Clock3 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api } from "../lib/api";
import { toast } from "sonner";

const DOCS = {
  privacy: { title: "سياسة الخصوصية", Icon: ShieldCheck, sections: [
    ["البيانات", "تستخدم المنصة البيانات التي يقدمها المستخدم لتشغيل الحسابات والطلبات والخدمات المطلوبة فقط."],
    ["الاحتفاظ والحذف", "تُحفظ البيانات في قاعدة تشغيل المنصة. لطلبات الوصول أو التصحيح أو الحذف، تواصل مع الدعم عبر القنوات المنشورة."],
    ["الذكاء الاصطناعي", "قد تُرسل المدخلات إلى مزود الذكاء المضبوط من مالك المنصة لمعالجتها. لا ترسل كلمات مرور أو مفاتيح أو بيانات مالية حساسة داخل المحادثات."],
    ["الأمن", "تُستخدم ضوابط تقنية لتقليل المخاطر، ولا يمكن ضمان أمن مطلق على الإنترنت."],
  ] },
  terms: { title: "الشروط والأحكام", Icon: FileText, sections: [
    ["الاستخدام", "يلتزم المستخدم بالقوانين والأنظمة المعمول بها، وألا يستخدم المنصة للاحتيال أو الإضرار بالغير أو انتهاك الحقوق."],
    ["المحتوى المنشأ", "المستخدم مسؤول عن مراجعة المحتوى والصفحات التي ينشئها قبل نشرها، بما في ذلك صحة المعلومات والحقوق والتراخيص."],
    ["المدفوعات", "تخضع المدفوعات والتسويات لتأكيد مزود الدفع أو الإدارة. رصيد المحفظة الداخلي ليس حسابًا مصرفيًا ولا يمثل وديعة مصرفية."],
    ["التوفر", "قد تتوقف خدمات تعتمد على مزودين خارجيين أو مفاتيح أو حصص استخدام، ولا تعد الخدمة غير المحدودة مضمونة."],
  ] },
  disclaimer: { title: "إخلاء المسؤولية", Icon: FileText, sections: [
    ["مخرجات الذكاء الاصطناعي", "المخرجات آلية وقد تحتوي أخطاء أو معلومات غير مكتملة. راجعها بشريًا قبل الاعتماد أو النشر."],
    ["المعاملات", "تحقق من هوية الأطراف وتفاصيل الصفقة خارج المنصة قبل الدفع أو مشاركة المعلومات الحساسة."],
    ["أسماء وخدمات الأطراف الأخرى", "التكاملات والبنى الخارجية تخضع لشروط مزوديها وتوفرهم وسياساتهم."],
    ["لا ضمان مطلق", "لا توجد حماية تمنع كل اختراق أو نسخ أو فقد بيانات. استخدم نسخًا احتياطية ومصادقة قوية وصلاحيات محدودة."],
  ] },
  support: { title: "مركز الدعم", Icon: MessageCircle, sections: [
    ["الدعم داخل المنصة", "استخدم بوت الدعم للمساعدة العامة، أو أرسل وصفًا للمشكلة دون تضمين كلمات مرور أو مفاتيح API."],
    ["التكاملات", "حالة أي تكامل تظهر في لوحة الإدارة. كلمة «مفعّل» تعني أن الحقول المطلوبة مضبوطة؛ اختبر الاتصال قبل الاعتماد عليه."],
    ["الإبلاغ الأمني", "أبلغ الإدارة عن مشكلة أمنية مع خطوات إعادة الإنتاج، دون استغلالها على بيانات أشخاص آخرين."],
  ] },
};

export function LegalPage({ page = "privacy" }) {
  const doc = DOCS[page] || DOCS.privacy;
  const Icon = doc.Icon;
  return (
    <main className="min-h-screen px-5 pb-20 pt-28" dir="rtl">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center gap-3 border-b border-white/10 pb-5">
          <Icon className="h-6 w-6 text-cyan" />
          <h1 className="font-display text-2xl font-bold text-white">{doc.title}</h1>
        </div>
        <nav className="flex flex-wrap gap-2 py-5" aria-label="صفحات المعلومات">
          {Object.entries(DOCS).map(([key, value]) => <Link key={key} to={`/legal/${key}`} className={`rounded-md border px-3 py-2 text-sm ${key === page ? "border-cyan/40 bg-cyan/10 text-cyan" : "border-white/10 text-muted-foreground hover:text-white"}`}>{value.title}</Link>)}
          <Link to="/tools" className="rounded-md border border-white/10 px-3 py-2 text-sm text-muted-foreground hover:text-white">الوقت والحاسبة</Link>
        </nav>
        <div className="divide-y divide-white/10">
          {doc.sections.map(([title, content]) => <section key={title} className="py-5"><h2 className="font-semibold text-white">{title}</h2><p className="mt-2 leading-7 text-muted-foreground">{content}</p></section>)}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">تاريخ المراجعة: {new Date().toLocaleDateString("ar-SA")}</p>
      </div>
    </main>
  );
}

export function ToolsPage() {
  const [left, setLeft] = useState("0");
  const [right, setRight] = useState("0");
  const [operator, setOperator] = useState("+");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [now, setNow] = useState(() => new Date());
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  React.useEffect(() => { const timer = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(timer); }, []);

  const a = Number(left); const b = Number(right);
  const result = operator === "+" ? a + b : operator === "-" ? a - b : operator === "×" ? a * b : b === 0 ? "لا يمكن القسمة على صفر" : a / b;
  const days = fromDate && toDate ? Math.round((Date.parse(`${toDate}T00:00:00Z`) - Date.parse(`${fromDate}T00:00:00Z`)) / 86400000) : null;
  const askSupport = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try { const response = await api.supportChat({ message: message.trim(), session_id: "tools-support" }); setReply(response.content || ""); }
    catch { toast.error("تعذّر الوصول إلى خدمة الدعم الآن"); }
    setSending(false);
  };

  return (
    <main className="min-h-screen px-5 pb-20 pt-28" dir="rtl">
      <div className="mx-auto max-w-5xl">
        <h1 className="font-display text-2xl font-bold text-white">أدوات الوقت والحساب والدعم</h1>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <section className="border-t-2 border-cyan/50 py-5">
            <h2 className="flex items-center gap-2 font-semibold text-white"><Clock3 className="h-5 w-5 text-cyan" /> الوقت الحالي</h2>
            <p className="mt-3 font-en text-2xl text-cyan" dir="ltr">{now.toLocaleTimeString("ar-SA", { timeZone: "Asia/Riyadh" })}</p>
            <p className="mt-1 text-sm text-muted-foreground">{now.toLocaleDateString("ar-SA", { dateStyle: "full", timeZone: "Asia/Riyadh" })}</p>
          </section>
          <section className="border-t-2 border-gold/50 py-5">
            <h2 className="flex items-center gap-2 font-semibold text-white"><Calculator className="h-5 w-5 text-gold" /> حاسبة أساسية</h2>
            <div className="mt-3 grid grid-cols-[1fr_80px_1fr] gap-2">
              <Input type="number" value={left} onChange={(event) => setLeft(event.target.value)} aria-label="العدد الأول" />
              <select value={operator} onChange={(event) => setOperator(event.target.value)} className="rounded-md border border-white/10 bg-[#101719] px-2 text-center text-white" aria-label="العملية"><option>+</option><option>-</option><option>×</option><option>÷</option></select>
              <Input type="number" value={right} onChange={(event) => setRight(event.target.value)} aria-label="العدد الثاني" />
            </div>
            <p className="mt-3 text-sm text-muted-foreground">الناتج</p><output className="font-en text-xl text-white" dir="ltr">{result}</output>
          </section>
          <section className="border-t-2 border-white/20 py-5 md:col-span-2">
            <h2 className="font-semibold text-white">الفارق بين تاريخين</h2>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="grid gap-1 text-xs text-muted-foreground">من<Input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} /></label>
              <label className="grid gap-1 text-xs text-muted-foreground">إلى<Input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} /></label>
              {days !== null && Number.isFinite(days) && <p className="pb-2 text-sm text-cyan">{Math.abs(days)} يوم {days < 0 ? "(التاريخ الأول بعد الثاني)" : ""}</p>}
            </div>
          </section>
          <section className="border-t-2 border-white/20 py-5 md:col-span-2">
            <h2 className="flex items-center gap-2 font-semibold text-white"><MessageCircle className="h-5 w-5 text-cyan" /> الدعم</h2>
            <div className="mt-3 flex gap-2"><Textarea rows={2} value={message} onChange={(event) => setMessage(event.target.value)} placeholder="صف المشكلة دون كتابة أي أسرار أو كلمات مرور" /><Button onClick={askSupport} disabled={sending || !message.trim()} className="btn-cyan">إرسال</Button></div>
            {reply && <p className="mt-3 whitespace-pre-wrap rounded-md border border-white/10 p-3 text-sm leading-6 text-white">{reply}</p>}
          </section>
        </div>
      </div>
    </main>
  );
}
